import { CheckCircleFilled, LoadingOutlined } from "@ant-design/icons";
import { Progress, Typography } from "antd";
import { createPortal } from "react-dom";
import "./scan-progress-modal.css";

const { Title, Text } = Typography;

type Props = {
  open: boolean;
  title: string;
  /** 固定阶段标题，与 `getScanProgressStepLabels` 顺序一致 */
  steps: string[];
  /** 当前进行到的阶段索引；等于 `steps.length` 表示全部勾选完成 */
  activeStepIndex: number;
  /** 0–100 */
  percent: number;
  /** 当前状态说明（长句，与阶段标题区分） */
  statusLine: string;
  isComplete: boolean;
  /** Portal 在 body 上，需显式传入与页面一致的主题 */
  variant?: "light" | "dark";
};

/**
 * 合规扫描全屏遮罩：分步清单 + 总进度条 + 动态状态文案（与 `scanClaims` 的 onProgress 配合）。
 */
export function ScanProgressModal({
  open,
  title,
  steps,
  activeStepIndex,
  percent,
  statusLine,
  isComplete,
  variant = "dark",
}: Props) {
  if (!open) {
    return null;
  }

  const node = (
    <div
      className={`scan-progress-overlay${variant === "light" ? " scan-progress-overlay--light" : ""}`}
      role="alertdialog"
      aria-modal="true"
      aria-busy={!isComplete}
      aria-live="polite"
    >
      <div className="scan-progress-card">
        <Title level={4} className="scan-progress-title">
          {title}
        </Title>

        <ul className="scan-progress-steps">
          {steps.map((label, i) => {
            const done = isComplete || i < activeStepIndex;
            const active = !isComplete && i === activeStepIndex;
            return (
              <li
                key={i}
                className={`scan-progress-step${done ? " scan-progress-step--done" : ""}${active ? " scan-progress-step--active" : ""}`}
              >
                <span className="scan-progress-step-icon" aria-hidden>
                  {done ? (
                    <CheckCircleFilled className="scan-progress-step-check" />
                  ) : active ? (
                    <LoadingOutlined spin className="scan-progress-step-spin" />
                  ) : (
                    <span className="scan-progress-step-dot" />
                  )}
                </span>
                <span className="scan-progress-step-label">{label}</span>
              </li>
            );
          })}
        </ul>

        <div className="scan-progress-bar-wrap">
          <div className="scan-progress-bar-head">
            <span className="scan-progress-pct">{Math.round(percent)}%</span>
          </div>
          <Progress
            percent={percent}
            showInfo={false}
            strokeColor={{
              "0%": "var(--scan-progress-from, #6366f1)",
              "100%": "var(--scan-progress-to, #22d3ee)",
            }}
            className="scan-progress-ant"
          />
        </div>

        <Text className="scan-progress-status" type="secondary">
          {statusLine}
        </Text>
      </div>
    </div>
  );

  return createPortal(node, document.body);
}
